import {useNavigate, useParams} from "react-router-dom";
import {useToast} from "../../modules/layout/components/ToastContext";
import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import AppBreadcrumb from "../../modules/layout/components/AppBreadcrumb";
import {Field, Form, Formik} from "formik";
import FormikSelect from '../../modules/layout/components/Select/FormikSelect';
import BackButton from "../../modules/layout/components/BackButton";
import {
    IAxiosGetResponse,
    IParamType,
    IParamValueType,
    IServiceDto,
    ITransactionParameterDto,
    ITransactionRouteDto
} from "../../interfaces/IUtilities";
import {create, getById, update} from "../_request";
import {AxiosResponse} from "axios";
import * as Yup from "yup";
import {ParamTypeText, ParamValueTypeText} from "../../helpers/EnumeratorHelper";
import Loader from "../../modules/layout/components/Loader";

const TransactionParameterForm: React.FC = () => {

    const {serviceid, transactionid, id} = useParams();
    const navigate = useNavigate();
    const isEdit = Boolean(id);
    const [service, setService] = useState({} as IServiceDto);
    const [transactionRoute, setTransactionRoute] = useState({} as ITransactionRouteDto);
    const {showToast} = useToast();
    const [loading, setLoading] = useState(true);
    const {t} = useTranslation();

    const [initialValues, setInitialValues] = useState<ITransactionParameterDto>({
        id: '00000000-0000-0000-0000-000000000000',
        transactionId: '',
        paramType: -1,
        paramName: '',
        paramValue: '',
        paramValueType: -1,
        isDeleted: false,
        isLibEditAllowed: false
    });

    const fetchServiceDetails = async () => {
        try {
            let result = await getById("/api/services/", serviceid!);
            let respStatus = result?.status || result?.response.status;

            if (respStatus === 200) {
                setService(result.data as IServiceDto);
            }
            setLoading(false);

        } catch (error) {
            setLoading(false);
            console.log(t('ConnectionError', {error: String(error)}));
            showToast(t('ConnectionError', {error: String(error)}), 'error');
        }
    }

    const fetchTransactionRoute = async () => {
        try {
            let result = await getById(`/api/transactionroutes/`, transactionid!);
            let respStatus = result?.status || result?.response.status;

            if (respStatus === 200) {
                setTransactionRoute(result.data as ITransactionRouteDto);
            } if (respStatus === 403) {
                navigate("/error/403");
            } else {
                throw new Error(t('StatusCode', {code: String(respStatus)}));
            }
        } catch (error) {
            setLoading(false);
            console.log(t('ConnectionError', {error: String(error)}));
        }
    }

    const handleSubmit = async (values: ITransactionParameterDto) => {
        if (isEdit) {
            setLoading(true);

            try {
                let result = await update('/api/transactionparameters/', {
                    ...values,
                    paramType: Number(values.paramType), // Manually change paramType to Number because the form returns string
                    paramValueType: Number(values.paramValueType), // Manually change paramValueType to Number because the form returns string
                }) as AxiosResponse;

                if (result.status === 200) {
                    setLoading(false);
                    showToast(t('TransactionParameterUpdatedSuccessfully'), 'success');
                    navigate(`/services/${serviceid}/transactions/${transactionid}/transaction-parameters/list`);
                } else if (result.status === 403) {
                    navigate('/error/403');
                } else if (result.status === 401) {
                    navigate('/auth/login');
                } else if (result.status === 500) {
                    navigate('/error/500');
                } else {
                    throw new Error(t('StatusCode', {code: String(result.status)}));
                }
            } catch (error) {
                setLoading(false);
                showToast(t('ConnectionError', {error: String(error)}), 'error');
            }

        } else {
            setLoading(true);

            try {
                let result = await create('/api/transactionparameters', {
                    ...values,
                    paramType: Number(values.paramType), // Manually change paramType to Number because the form returns string
                    paramValueType: Number(values.paramValueType), // Manually change paramValueType to Number because the form returns string
                    transactionId: transactionid
                }) as AxiosResponse;

                if (result.status === 200) {
                    setLoading(false);
                    showToast(t('TransactionParameterCreatedSuccessfully'), 'success');
                    navigate(`/services/${serviceid}/transactions/${transactionid}/transaction-parameters/list`);
                } else if (result.status === 400 && result.data?.errors) {
                    setLoading(false);

                    for (const key in result.data.errors) {
                        showToast(t(key), 'error');
                    }

                } else if (result.status === 403) {
                    navigate('/error/403');
                } else if (result.status === 401) {
                    navigate('/auth/login');
                } else if (result.status === 500) {
                    navigate('/error/500');
                } else {
                    throw new Error(t('StatusCode', {code: String(result.status)}));
                }
            } catch (error) {
                setLoading(false);
                showToast(t('ConnectionError', {error: String(error)}), 'error');
            }
        }
    };

    const validationSchema = Yup.object({
        paramType: Yup.number()
            .notOneOf([IParamType.Unknown],  t('PleaseSubmitAValue'))
            .required(t('Required')),
        paramName: Yup.string().required(t('Required')),
        paramValue: Yup.string().required(t('Required')),
        paramValueType: Yup.number()
            .notOneOf([IParamValueType.Unknown],  t('PleaseSubmitAValue'))
            .required(t('Required')),
    });

    const paramTypeOptions = React.useMemo(() => [
        { value: IParamType.AuthParam, label: ParamTypeText[IParamType.AuthParam] },
        { value: IParamType.HeaderParam, label: ParamTypeText[IParamType.HeaderParam] },
    ], [t]);

    const paramValueTypeOptions = React.useMemo(() => [
        { value: IParamValueType.Bool, label: ParamValueTypeText[IParamValueType.Bool] },
        { value: IParamValueType.Int, label: ParamValueTypeText[IParamValueType.Int] },
        { value: IParamValueType.Long, label: ParamValueTypeText[IParamValueType.Long] },
        { value: IParamValueType.Decimal, label: ParamValueTypeText[IParamValueType.Decimal] },
        { value: IParamValueType.String, label: ParamValueTypeText[IParamValueType.String] },
    ], [t]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                let result = await getById("/api/transactionparameters/", id!);
                let respStatus = result?.status || result?.response.status;

                if (respStatus === 200) {
                    setInitialValues(result.data);
                    setLoading(false);
                } else if (respStatus === 403) {
                    navigate("/error/403");
                } else if (respStatus === 401) {
                    navigate("/auth/login");
                } else if (respStatus === 500) {
                    navigate("/error/500");
                } else {
                    throw new Error(t('StatusCode', {code: String(respStatus)}));
                }
            } catch (error) {
                setLoading(false);
                console.log(t('ConnectionError', {error: String(error)}));
                showToast(t('ConnectionError', {error: String(error)}), 'error');
                navigate("/services/list");
            }
        }

        fetchServiceDetails();
        fetchTransactionRoute();

        if (isEdit && serviceid) {
            fetchData();
        } else {
            setLoading(false);
        }
    }, [serviceid, isEdit]);

    return (
        <div className="container mt-4 gg-container-form">
            <AppBreadcrumb
                items={[
                    {title: t('Home'), href: "/"},
                    {title: t('Services'), href: '/services/list'},
                    {
                        title: t('BreadcrumbServiceTransationRoutes', { serviceName: service.name}),
                        href: `/services/${serviceid}/transactions/list`
                    },
                    {
                        title: t('BreadcrumbRouteLabelTransactionParameters', {routeLabel: transactionRoute.routeLabel}),
                        href: `/services/${serviceid}/transactions/${transactionid}/transaction-parameters/list`
                    },
                    {title: isEdit ? t('EditTransactionParameter') : t('Create Transaction Parameter')} // Active page (no href)
                ]}/>

            <h3>{isEdit ? t('EditTransactionParameter') : t('Create Transaction Parameter')}</h3>


            <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={validationSchema}
                        enableReinitialize>
                {({errors, touched, isSubmitting, setFieldValue}) => (
                    <Form>
                        {isSubmitting && (
                            <Loader />
                        )}

                        <div className="mb-3">
                            <label className="form-label mb-2">{t('ParameterType')}</label>
                            <FormikSelect
                                name="paramType"
                                options={paramTypeOptions}
                                isClearable
                                clearValue={IParamType.Unknown}
                                isDisabled={loading}
                                placeholder={`-- ${t('Select')} --`}
                            />
                            {touched.paramType && errors.paramType &&
                                <div className="text-danger">{errors.paramType}</div>}
                        </div>

                        <div className="mb-3">
                            <label className="form-label">{t('ParameterName')}</label>
                            <Field className="form-control" name="paramName"/>
                            {touched.paramName && errors.paramName &&
                                <div className="text-danger">{errors.paramName}</div>}
                        </div>

                        <div className="mb-3">
                            <label className="form-label">{t('ParameterValue')}</label>
                            <Field className="form-control" name="paramValue"/>
                            {touched.paramValue && errors.paramValue &&
                                <div className="text-danger">{errors.paramValue}</div>}
                        </div>

                        <div className="mb-3">
                            <label className="form-label mb-2">{t('ParameterValueType')}</label>
                            <FormikSelect
                                name="paramValueType"
                                options={paramValueTypeOptions}
                                isClearable
                                clearValue={IParamValueType.Unknown}
                                isDisabled={loading}
                                placeholder={`-- ${t('Select')} --`}
                            />
                            {touched.paramValueType && errors.paramValueType &&
                                <div className="text-danger">{errors.paramValueType}</div>}
                        </div>

                        <div className="mb-3">
                            <label>
                                <Field className="form-check-input me-2" type="checkbox"
                                    name="isLibEditAllowed" />
                                {t('InLibEditAllowed')}
                            </label>
                            {touched.isLibEditAllowed && errors.isLibEditAllowed &&
                                <div className="text-danger">{errors.isLibEditAllowed}</div>}
                        </div>

                        <div className="mb-3">
                            <BackButton
                                goTo={`/services/${serviceid}/transactions/${transactionid}/transaction-parameters/list`}/>

                            <button type="submit" className="btn ms-3 gg-btn-submit">
                                {isEdit ? t('Update') : t('Create')}
                            </button>
                        </div>

                    </Form>
                )}
            </Formik>

        </div>
    )
}
export default TransactionParameterForm;
